
import { computed, defineComponent, ref } from "vue-demi";
import { SMTasks } from "@/store";
import pdf from "@teckel/vue-pdf";

export default defineComponent({
  name: "UploaderPopup",
  components: {
    NewPopup: () => import("@/modules/Documents/NewPopup.vue"),
    FileView: () => import("@/modules/Documents/FileView.vue"),
    pdf,
    PDFViewerToolbarPageControl: () =>
      import("@/modules/Documents/PDFViewerToolbarPageControl.vue"),
    PDFViewerToolbarZoomControl: () =>
      import("@/modules/Documents/PDFViewerToolbarZoomControl.vue"),
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      // save_to_directory saves within the uploader into directory
      // task_template saves on finish task
      default: "save_to_directory",
    },
    directoryId: {
      type: Number,
      default: 0,
    },
    fileType: {
      type: String,
      default: "default",
    },
    name: {
      type: String,
      default: "Upload document",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "update:modelValue",
    "open-parsed-document-popup",
    "cancel",
    "ok",
    "files",
    "set-additional-data",
    "set-date",
  ],

  setup(props, { emit }) {
    const taskTemplate = computed(() => SMTasks.taskTemplate);

    const title = computed(() => taskTemplate.value?.name || props.name);

    type ValidatedFile = File & { error?: string };

    const files = ref<ValidatedFile[]>([]);
    const fileInput = ref<HTMLInputElement | null>(null);
    const fileUrls = ref<string[]>([]);

    const isDraggedOver = ref(false);

    const selectedFileIndex = ref(0);

    const accept = computed(() => {
      // return "application/pdf"
      return "";
    });

    const fileType = computed(
      () => taskTemplate.value?.document_template_v2?.file_type || props.fileType
    );

    const isParsedFileType = computed(() => {
      return ["cos", "ielts", "pte"].includes(fileType.value);
    });

    const isMonthlyUploadFileType = computed(() => {
      return fileType.value === "monthly_upload";
    });

    const isDateFileType = computed(() => {
      return ["date_provided", "date_provided_last_rtwc", "date_provided_previous_rtwc"].includes(
        fileType.value
      );
    });

    const isMultipleUpload = computed(() => {
      return !isParsedFileType.value;
    });

    const showDropzone = computed(() => {
      if (isParsedFileType.value && files.value.length) return false;

      return true;
    });

    const MAX_SIZE_MB = 2 * 1024 * 1024; // 2MB in bytes

    const ACCEPTED_FILE_TYPE = "application/pdf";

    const hasError = ref(false);

    function validateFiles(payload: File[]) {
      hasError.value = false;

      const seenFiles = new Set<string>();

      files.value = payload.map((f) => {
        let error = "";

        if (f.type !== ACCEPTED_FILE_TYPE) {
          error = "Upload only PDF files";
        } else if (f.size > MAX_SIZE_MB) {
          error = "Size must not exceed 2 MB";
        } else {
          const fileIdentifier = `${f.name}-${f.size}`;

          if (seenFiles.has(fileIdentifier)) {
            error = "Uploaded twice";
          } else {
            seenFiles.add(fileIdentifier);
          }
        }

        if (error) {
          hasError.value = true;
        }

        return Object.assign(f, { error });
      });
    }

    const errorMessageViewer = computed(() => {
      const file = files.value[selectedFileIndex.value];

      if (!file) return "";

      if (file.error === "Size must not exceed 2 MB") {
        return "size";
      } else if (file.error === "Upload only PDF files") {
        return "format";
      } else if (file.error === "Uploaded twice") {
        return "twice";
      }

      return "";
    });

    function onFiles(payload: File[]) {
      emit("files", payload);

      files.value = payload;

      validateFiles(payload);
    }

    const okLabel = computed(() => {
      if (props.type === "task_template") {
        return "Done";
      } else if (props.type === "save_to_directory") {
        return "Save";
      }
    });

    const checksComputed = computed(() => {
      const checksString = taskTemplate.value?.document_template_v2?.checks;

      if (!checksString) return [];

      return checksString.split(",,").map((label) => ({ label, checked: null }));
    });

    const checks = ref(checksComputed.value);

    // Uploader
    function selectFile() {
      fileInput.value?.click();
    }

    function saveFile(file: File) {
      files.value.push(file);
      fileUrls.value.push(URL.createObjectURL(file));

      onFiles(files.value);
    }

    function reuploadFile(index: number) {
      if (!fileInput.value) return;

      fileInput.value.dataset.index = index.toString();

      fileInput.value.click();
    }

    function pickFile(e: Event) {
      const input = e.target as HTMLInputElement;
      if (!input.files?.length) return;

      const replaceIndex = input.dataset.index ? parseInt(input.dataset.index, 10) : null;

      if (isMultipleUpload.value) {
        Array.from(input.files).forEach((file, i) => {
          if (replaceIndex !== null && i === 0) {
            URL.revokeObjectURL(fileUrls.value[replaceIndex]);

            files.value.splice(replaceIndex, 1, file);
            fileUrls.value.splice(replaceIndex, 1, URL.createObjectURL(file));
            selectedFileIndex.value = replaceIndex;

            validateFiles([file]);
          } else {
            saveFile(file);
          }
        });
      } else {
        const newFile = input.files[0];
        if (files.value.length > 0) {
          URL.revokeObjectURL(fileUrls.value[0]);
        }

        files.value = [newFile];
        fileUrls.value = [URL.createObjectURL(newFile)];
        selectedFileIndex.value = 0;
        validateFiles([newFile]);
      }

      input.value = "";
      input.removeAttribute("data-index");
    }

    function onDragOver() {
      isDraggedOver.value = true;
    }

    function onDragLeave() {
      isDraggedOver.value = false;
    }

    function onDrop(e: DragEvent) {
      e.preventDefault();

      const fileList = e.dataTransfer?.files;
      if (!fileList || fileList.length === 0) return;

      if (isMultipleUpload.value) {
        Array.from(fileList).forEach((file) => {
          saveFile(file);
        });
      } else {
        const newFile = fileList[0];

        if (files.value.length > 0) {
          URL.revokeObjectURL(fileUrls.value[0]);
        }

        files.value = [newFile];
        fileUrls.value = [URL.createObjectURL(newFile)];
        selectedFileIndex.value = 0;
      }

      onDragLeave();
    }

    function formatBytes(bytes: number, decimals = 0) {
      if (!bytes) return "0 b";
      const k = 1024,
        sizes = ["b", "kb", "mb", "gb", "tb"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${(bytes / Math.pow(k, i)).toFixed(decimals)} ${sizes[i]}`;
    }

    const onFileDelete = (index: number) => {
      if (index >= 0 && index < files.value.length) {
        URL.revokeObjectURL(fileUrls.value[index]);
        files.value.splice(index, 1);
        fileUrls.value.splice(index, 1);
        validateFiles(files.value);
      }
    };

    const checkOptions = [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ];

    const isSaveDisabled = computed(() => {
      if (!files.value.length) return true;

      if (checks.value.some((check) => !check.checked)) return true;

      if (hasError.value) return true;

      return false;
    });

    // Date picker
    const date = ref(null);

    // Month Year Picker
    const currentYear = new Date().getFullYear();
    const year = ref(currentYear);

    const yearOptions = computed(() => {
      return Array.from({ length: 12 }, (_, i) => {
        const yearValue = currentYear - 10 + i;
        return { value: yearValue.toString(), label: yearValue.toString() };
      });
    });

    const monthOptions = computed(() => {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      return months.map((month) => ({
        value: month,
        label: month,
      }));
    });

    const month = ref(monthOptions.value[new Date().getMonth()].value);

    // Actions
    function close() {
      emit("update:modelValue", false);
    }

    function onOk() {
      if (isParsedFileType.value) {
        emit("open-parsed-document-popup", files.value);

        close();

        return;
      }

      if (isMonthlyUploadFileType.value) {
        emit("set-additional-data", {
          year,
          month,
        });
      }

      if (isDateFileType.value) {
        emit("set-date", date);
      }

      emit("ok", files.value);

      // close();
    }

    function onCancel() {
      emit("cancel", !files.value.length);

      // close();
    }

    // PDF preview
    const DEFAULT_PREVIEW = {
      link: "",
      name: "",
      currentPage: 1,
      pageCount: 1,
      zoom: 100,
      minZoom: 100,
      maxZoom: 500,
    };

    const preview = ref({
      link: "",
      name: "",
      currentPage: 1,
      pageCount: 1,
      zoom: 100,
      minZoom: 100,
      maxZoom: 500,
    });

    function setPageCount(payload) {
      preview.value.pageCount = payload;
    }

    function setCurrentPage(payload) {
      preview.value.currentPage = payload;
    }

    function handlePdfError(e) {
      console.log("error in pdf viewer: ", e);
    }

    const computedPreviewStyle = computed(() => {
      return {
        transform: `scale(${preview.value.zoom / 100})`,
        transformOrigin: "top left",
        maxWidth: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        transition: "transform 0.2s ease-in-out",
      };
    });

    function updatePreview(payload) {
      preview.value = { ...payload };
    }

    function selectFileIndex(index) {
      selectedFileIndex.value = index;

      updatePreview(DEFAULT_PREVIEW);
    }

    return {
      onCancel,
      onOk,
      onFiles,
      okLabel,
      title,
      fileInput,
      files,
      fileUrls,
      isDraggedOver,
      accept,
      selectFile,
      pickFile,
      reuploadFile,
      onDrop,
      onDragOver,
      onDragLeave,
      formatBytes,
      selectedFileIndex,
      onFileDelete,
      isParsedFileType,
      isMonthlyUploadFileType,
      isDateFileType,
      checks,
      checkOptions,
      isSaveDisabled,
      yearOptions,
      monthOptions,
      year,
      month,
      date,
      hasError,
      errorMessageViewer,
      isMultipleUpload,
      preview,
      setPageCount,
      setCurrentPage,
      computedPreviewStyle,
      updatePreview,
      selectFileIndex,
      showDropzone,
      handlePdfError,
    };
  },
});
